import type {
  GetBarPixelCenter,
  GetLinearSeriesPixelCenter,
  SortedEntriesPerShape,
  SortedEntry,
} from './build-sorted-entries.js';
import type { QueryAllHovered } from './query-hovered.js';
import { getClusterBoundariesAtPosition } from '../../core/utils/binary-search.js';
import { safeGet } from '../../core/utils/safe-get.js';
import type {
  BarSeriesQueryResult,
  HoverQuery,
  HoverQueryArgs,
  HoverQueryResult,
  SeriesQueryResult,
} from '../context/XYChartOverlayPerformance.context.js';
import type {
  AreaSeriesWithDatapoints,
  BarSeriesWithDatapoints,
  LineSeriesWithDatapoints,
  MapDatapoints,
} from '../types/xy-chart-internals.js';
import type { AreaDatapoint, LineDatapoint } from '../types/xy-chart.js';
export function buildQueryShared(
  sortedEntries: SortedEntriesPerShape | undefined,
  queryAllHovered: QueryAllHovered,
  getBarPixelCenter: GetBarPixelCenter,
  getLinearSeriesPixelCenter: GetLinearSeriesPixelCenter,
  datapointMetadataMap: MapDatapoints,
): HoverQuery {
  return function queryShared({
    position,
    rectSeriesData,
    barSeriesData,
    lineSeriesData,
    areaSeriesData,
  }: HoverQueryArgs): HoverQueryResult {
    const relative = position.relative;

    const allRectsHovered = queryAllHovered(rectSeriesData, relative);
    if (allRectsHovered.length > 0) {
      return {
        preferred: allRectsHovered.at(-1),
        datapoints: allRectsHovered,
      };
    }

    const allBarsHovered = queryAllHovered(
      barSeriesData,
      relative,
    ) as BarSeriesQueryResult[];

    const lastHovered = allBarsHovered.at(-1);
    const searchX = lastHovered
      ? getBarPixelCenter(lastHovered.series, lastHovered.datapoint).xPixel
      : relative.x;

    // The sorted-entry build is debounced and `undefined` during the
    // first few frames of a render. Fall back to a linear scan over all
    // series so the tooltip works immediately on first hover.
    const clusterEntries = sortedEntries
      ? findClosestClusterBinary(sortedEntries.combined, searchX)
      : findClosestClusterLinear(
          barSeriesData,
          lineSeriesData,
          areaSeriesData,
          datapointMetadataMap,
          getBarPixelCenter,
          getLinearSeriesPixelCenter,
          searchX,
        );

    if (clusterEntries.length === 0) {
      return { preferred: undefined, datapoints: [] };
    }

    let closestEntry: SortedEntry | undefined;

    if (allBarsHovered.length === 0) {
      const cursorY = relative.y;
      closestEntry = clusterEntries.reduce<SortedEntry | undefined>(
        (closest, entry) =>
          closest === undefined ||
          Math.abs(entry.yPixel - cursorY) < Math.abs(closest.yPixel - cursorY)
            ? entry
            : closest,
        undefined,
      );
    }

    const preferred = closestEntry?.result ?? allBarsHovered.at(-1);

    let allowedBoundaries: Set<string> | undefined;
    if (allBarsHovered.length > 0) {
      allowedBoundaries = new Set(
        allBarsHovered.map(
          (b) => `${String(b.datapoint.x0)}-${String(b.datapoint.x1)}`,
        ),
      );
    } else if (preferred) {
      allowedBoundaries = new Set([
        `${String(preferred.datapoint.x0)}-${String(preferred.datapoint.x1)}`,
      ]);
    }

    const filteredEntries = clusterEntries.filter(
      (e) =>
        allowedBoundaries === undefined || allowedBoundaries.has(e.boundaries),
    );
    const datapoints: SeriesQueryResult[] = filteredEntries.map(
      (e) => e.result,
    );

    return { preferred, datapoints };
  };
}

function findClosestClusterBinary(
  sorted: SortedEntry[],
  searchX: number,
): SortedEntry[] {
  if (sorted.length === 0) {
    return [];
  }

  const [left, right] = getClusterBoundariesAtPosition(
    searchX,
    sorted,
    (e) => e.xPixel,
    (e) => Math.round(e.xPixel),
  );

  if (left === -1) {
    return [];
  }

  return sorted.slice(left, right + 1);
}

function findClosestClusterLinear(
  barSeriesData: BarSeriesWithDatapoints[],
  lineSeriesData: LineSeriesWithDatapoints[],
  areaSeriesData: AreaSeriesWithDatapoints[],
  datapointMetadataMap: MapDatapoints,
  getBarPixelCenter: GetBarPixelCenter,
  getLinearSeriesPixelCenter: GetLinearSeriesPixelCenter,
  searchX: number,
): SortedEntry[] {
  const entries: SortedEntry[] = [];
  let closestKey: number | undefined;
  let closestDistance = Infinity;

  function visit(
    xPixel: number,
    yPixel: number,
    boundaries: string,
    result: SeriesQueryResult,
  ) {
    entries.push({ xPixel, yPixel, boundaries, result });
    const distance = Math.abs(xPixel - searchX);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestKey = Math.round(xPixel);
    }
  }

  for (const series of barSeriesData) {
    for (const datapoint of series.data) {
      if (safeGet(datapointMetadataMap, datapoint).isGap) {
        continue;
      }

      const { xPixel, yPixel, boundaries } = getBarPixelCenter(
        series,
        datapoint,
      );
      visit(xPixel, yPixel, boundaries, {
        type: series.type,
        distance: 0,
        datapoint,
        series,
      });
    }
  }

  for (const series of [...lineSeriesData, ...areaSeriesData]) {
    for (const datapoint of series.data as (LineDatapoint | AreaDatapoint)[]) {
      if (safeGet(datapointMetadataMap, datapoint).isGap) {
        continue;
      }
      const { xPixel, yPixel, boundaries } = getLinearSeriesPixelCenter(
        series,
        datapoint,
      );
      visit(xPixel, yPixel, boundaries, {
        type: series.type,
        distance: 0,
        datapoint,
        series,
      } as unknown as SeriesQueryResult);
    }
  }

  if (closestKey === undefined) {
    return [];
  }
  return entries.filter((e) => Math.round(e.xPixel) === closestKey);
}
